import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invalidateInventoryQueries } from "@/lib/mutationHelpers";
import { queryKeys } from "@/lib/queryKeys";
import { guestSentence, isLinenName, type GuestGuideKind } from "@/lib/guestGuideCopy";
import { syncGuestGuideKnowledge } from "@/lib/guestGuideHospitable";
import { normalizeRoomName } from "@/lib/roomUtils";
import type { Appliance } from "@/hooks/useAppliances";

export type GuestGuideStatus = "pending" | "yes" | "no";

export type GuestGuideCard = {
  id: string;
  source: "inventory" | "appliance";
  kind: GuestGuideKind;
  name: string;
  room: string;
  photoUrl: string | null;
  defaultQty: number | null;
  guest_visible: boolean | null;
  guest_set_qty: number | null;
  guest_place: string | null;
  guest_place_photo_url: string | null;
  guest_copy: string | null;
  guest_guide_status: GuestGuideStatus;
  hospitable_knowledge_item_id: string | null;
};

export function inventoryToGuideCard(
  row: Record<string, unknown>,
  roomName: string,
): GuestGuideCard {
  const status = (row.guest_guide_status as GuestGuideStatus) || "pending";
  const count = typeof row.current_count === "number" ? row.current_count : null;
  return {
    id: String(row.id),
    source: "inventory",
    kind: "inventory",
    name: String(row.item_name || ""),
    room: roomName,
    photoUrl: (row.photo_url as string | null) || null,
    defaultQty: count,
    guest_visible: (row.guest_visible as boolean | null) ?? null,
    guest_set_qty: (row.guest_set_qty as number | null) ?? null,
    guest_place: (row.guest_place as string | null) ?? null,
    guest_place_photo_url: (row.guest_place_photo_url as string | null) ?? null,
    guest_copy: (row.guest_copy as string | null) ?? null,
    guest_guide_status: status,
    hospitable_knowledge_item_id: (row.hospitable_knowledge_item_id as string | null) ?? null,
  };
}

export function applianceToGuideCard(row: Appliance): GuestGuideCard {
  const extra = row as Appliance & {
    guest_visible?: boolean | null;
    guest_set_qty?: number | null;
    guest_place?: string | null;
    guest_place_photo_url?: string | null;
    guest_copy?: string | null;
    guest_guide_status?: GuestGuideStatus | null;
    hospitable_knowledge_item_id?: string | null;
  };
  return {
    id: row.id,
    source: "appliance",
    kind: "appliance",
    name: row.name,
    room: row.location || "",
    photoUrl: Array.isArray(row.photo_urls) && row.photo_urls[0] ? String(row.photo_urls[0]) : null,
    defaultQty: null,
    guest_visible: extra.guest_visible ?? null,
    guest_set_qty: extra.guest_set_qty ?? null,
    guest_place: extra.guest_place ?? null,
    guest_place_photo_url: extra.guest_place_photo_url ?? null,
    guest_copy: extra.guest_copy ?? null,
    guest_guide_status: extra.guest_guide_status || "pending",
    hospitable_knowledge_item_id: extra.hospitable_knowledge_item_id ?? null,
  };
}

export function defaultGuestQty(card: Pick<GuestGuideCard, "name" | "defaultQty">): number {
  if (isLinenName(card.name)) return 1;
  return card.defaultQty && card.defaultQty > 0 ? Math.round(card.defaultQty) : 1;
}

/** Pending inventory only. Lamps/appliances never enter extras. */
export function buildGuestGuideQueue(
  inventory: Record<string, unknown>[],
  rooms: { id: string; name: string; property_room_id?: string | null }[],
  filter?: { roomName?: string | null; propertyRoomId?: string | null },
): GuestGuideCard[] {
  const roomById = new Map(rooms.map((r) => [r.id, r]));
  const wantName = filter?.roomName ? normalizeRoomName(filter.roomName) : "";
  const wantPropertyRoom = filter?.propertyRoomId || "";
  return inventory
    .map((row) => {
      const roomId = String(row.room_id || "");
      const ir = roomById.get(roomId);
      return { card: inventoryToGuideCard(row, ir?.name || ""), invRoom: ir };
    })
    .filter(({ card, invRoom }) => {
      if (card.guest_guide_status !== "pending") return false;
      if (!wantName && !wantPropertyRoom) return true;
      if (wantPropertyRoom && invRoom?.property_room_id === wantPropertyRoom) return true;
      if (wantName && normalizeRoomName(card.room) === wantName) return true;
      return false;
    })
    .map(({ card }) => card)
    .sort((a, b) => a.room.localeCompare(b.room) || a.name.localeCompare(b.name));
}

export function decidedGuestGuideCards(
  inventory: Record<string, unknown>[],
  rooms: { id: string; name: string; property_room_id?: string | null }[],
  filter?: { roomName?: string | null; propertyRoomId?: string | null },
): GuestGuideCard[] {
  const roomById = new Map(rooms.map((r) => [r.id, r]));
  const wantName = filter?.roomName ? normalizeRoomName(filter.roomName) : "";
  const wantPropertyRoom = filter?.propertyRoomId || "";
  return inventory
    .map((row) => {
      const ir = roomById.get(String(row.room_id || ""));
      return { card: inventoryToGuideCard(row, ir?.name || ""), invRoom: ir };
    })
    .filter(({ card, invRoom }) => {
      if (card.guest_guide_status === "pending") return false;
      if (!wantName && !wantPropertyRoom) return true;
      if (wantPropertyRoom && invRoom?.property_room_id === wantPropertyRoom) return true;
      if (wantName && normalizeRoomName(card.room) === wantName) return true;
      return false;
    })
    .map(({ card }) => card)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export type GuestGuideSave = {
  propertyId: string;
  card: GuestGuideCard;
  visible: boolean;
  qty: number | null;
  place: string;
  placePhotoUrl: string | null;
};

function payloadFromSave(input: GuestGuideSave) {
  if (!input.visible) {
    return {
      guest_visible: false,
      guest_set_qty: null,
      guest_place: null,
      guest_place_photo_url: null,
      guest_copy: null,
      guest_guide_status: "no" as const,
    };
  }
  const copy = guestSentence({
    kind: input.card.kind,
    name: input.card.name,
    qty: input.card.kind === "inventory" ? input.qty : null,
    place: input.place,
  });
  return {
    guest_visible: true,
    guest_set_qty: input.card.kind === "inventory" ? input.qty : null,
    guest_place: input.place,
    guest_place_photo_url: input.placePhotoUrl,
    guest_copy: copy,
    guest_guide_status: "yes" as const,
  };
}

export function useSaveGuestGuide() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: GuestGuideSave) => {
      const payload = payloadFromSave(input);
      if (input.card.source === "inventory") {
        const { error } = await supabase.from("inventory_items").update(payload).eq("id", input.card.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("appliances").update(payload).eq("id", input.card.id);
        if (error) throw error;
      }
      // Always sync: create/update when visible, delete from Hospitable when cleared.
      await syncGuestGuideKnowledge({
        propertyId: input.propertyId,
        source: input.card.source,
        itemId: input.card.id,
        content: payload.guest_copy,
      });
      return { ...input, copy: payload.guest_copy, visible: payload.guest_visible };
    },
    onSuccess: async (res) => {
      queryClient.setQueryData(queryKeys.inventory.byProperty(res.propertyId), (old: unknown) => {
        const list = Array.isArray(old) ? old : [];
        return list.map((item: { id?: string }) =>
          item.id === res.card.id
            ? {
                ...item,
                guest_visible: res.visible,
                guest_set_qty: res.qty,
                guest_place: res.place,
                guest_place_photo_url: res.placePhotoUrl,
                guest_copy: res.copy,
                guest_guide_status: res.visible ? "yes" : "no",
                ...(res.visible ? {} : { hospitable_knowledge_item_id: null }),
              }
            : item,
        );
      });
      await invalidateInventoryQueries(queryClient, res.propertyId);
      queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.appliances(res.propertyId) });
    },
  });
}
